import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Coins, Loader2, Save, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CashHoursPanel({ weekStart, onSaved }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});   // {employee_code: hours}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payroll/cash-hours/${weekStart}`);
      setData(r.data);
      const seed = {};
      (r.data.entries || []).forEach(e => { seed[e.employee_code] = e.hours || ""; });
      setEdits(seed);
    } catch (err) {
      toast.error("Cash hours load failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (weekStart) load(); }, [weekStart]);

  const save = async () => {
    setSaving(true);
    try {
      const entries = (data.entries || []).map(e => ({
        employee_code: e.employee_code,
        hours: parseFloat(edits[e.employee_code] || 0),
      }));
      await axios.post(`${API}/payroll/cash-hours`, { week_start: weekStart, entries });
      toast.success("✅ Cash hours saved. Labor Spend will update.");
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading cash hours…</div>;
  }

  if (!data.entries || data.entries.length === 0) return null;

  // Compute projected total (live preview of what they're about to save)
  let projHours = 0, projCost = 0;
  data.entries.forEach(e => {
    const h = parseFloat(edits[e.employee_code] || 0);
    projHours += h;
    projCost += h * (e.hourly_rate || 0);
  });
  const dirty = JSON.stringify(edits) !== JSON.stringify(
    Object.fromEntries(data.entries.map(e => [e.employee_code, e.hours || ""]))
  );

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-amber-800" />
          <h3 className="font-bold text-base text-amber-900">Cash Hours This Week</h3>
          <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-bold">{data.entries.length}</span>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-amber-700">Projected</p>
          <p className="font-bold text-amber-900">{projHours.toFixed(1)}h · ${projCost.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
        </div>
      </div>

      <p className="text-xs text-amber-800 mb-3 leading-snug">
        <Lock className="w-3 h-3 inline mr-1" />
        Cash employees are <strong>locked off the schedule</strong>. Enter their actual hours below — they flow into Labor Spend but NEVER touch W-2/1099 payroll.
      </p>

      <div className="bg-white rounded border border-amber-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-amber-100/50 text-amber-900 text-xs">
            <tr>
              <th className="text-left px-2 py-1.5">Employee</th>
              <th className="text-left px-2 py-1.5">Dept</th>
              <th className="text-right px-2 py-1.5">Rate</th>
              <th className="text-right px-2 py-1.5 w-32">Hours</th>
              <th className="text-right px-2 py-1.5">Est. Cash</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map(e => {
              const h = parseFloat(edits[e.employee_code] || 0);
              const cost = h * (e.hourly_rate || 0);
              return (
                <tr key={e.employee_code} className="border-b border-amber-50">
                  <td className="px-2 py-1.5 font-medium">{e.name} <span className="text-stone-400 text-xs">#{e.employee_code}</span></td>
                  <td className="px-2 py-1.5 text-xs text-stone-600">{e.department}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">${e.hourly_rate}/h</td>
                  <td className="px-2 py-1.5">
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      max="100"
                      value={edits[e.employee_code] ?? ""}
                      onChange={ev => setEdits(prev => ({ ...prev, [e.employee_code]: ev.target.value }))}
                      className="h-8 text-right font-mono"
                      placeholder="0"
                      data-testid={`cash-hours-${e.employee_code}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-amber-900">${cost.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={load} disabled={saving} className="border-amber-300">
          <RefreshCw className="w-4 h-4 mr-1" /> Reload
        </Button>
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className="bg-amber-700 hover:bg-amber-800 text-white"
          data-testid="cash-hours-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {dirty ? "Save Cash Hours" : "Saved ✓"}
        </Button>
      </div>
    </div>
  );
}
